import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { INVALID_SELECTOR } from "./reveal";

export function Tabs({ tabs }: { tabs: { key: string; label: string; content: ReactNode }[] }) {
  const [active, setActive] = useState(tabs[0]?.key);
  const base = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const panels = useRef<Record<string, HTMLDivElement | null>>({});
  const hadInvalid = useRef<Record<string, boolean>>({});
  // Se a aba ativa não tem erro e outra passou a ter, mostra a outra (FE11).
  // Roda a cada render de propósito (lê o DOM dos filhos); o setState só ocorre na transição sem erro → com erro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    let target: string | undefined;
    for (const t of tabs) {
      const has = !!panels.current[t.key]?.querySelector(INVALID_SELECTOR);
      if (has && !hadInvalid.current[t.key] && !target) target = t.key;
      hadInvalid.current[t.key] = has;
    }
    const activeHas = active ? !!panels.current[active]?.querySelector(INVALID_SELECTOR) : false;
    if (target && !activeHas) setActive(target);
  });
  const onKey = (e: KeyboardEvent, i: number) => {
    const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!d) return;
    const next = (i + d + tabs.length) % tabs.length;
    setActive(tabs[next]!.key);
    refs.current[next]?.focus();
  };
  return (
    <>
      <ul className="nav nav-tabs mb-3" role="tablist">
        {tabs.map((t, i) => (
          <li className="nav-item" role="presentation" key={t.key}>
            <button
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-t-${t.key}`}
              aria-controls={`${base}-p-${t.key}`}
              aria-selected={active === t.key}
              tabIndex={active === t.key ? 0 : -1}
              className={`nav-link${active === t.key ? " active" : ""}`}
              onClick={() => setActive(t.key)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>
      {tabs.map((t) => (
        <div
          key={t.key}
          ref={(el) => {
            panels.current[t.key] = el;
          }}
          role="tabpanel"
          id={`${base}-p-${t.key}`}
          aria-labelledby={`${base}-t-${t.key}`}
          hidden={active !== t.key}
        >
          {t.content}
        </div>
      ))}
    </>
  );
}
